import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Save, FileText, Check, Building, Package, Search } from 'lucide-react';
import { useBusiness } from '../../contexts/BusinessContext';
import api from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';

const CreatePurchaseReceivePage = () => {
  const { businessId, receiveId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { activeBusiness } = useBusiness();
  const isEditing = !!receiveId;
  const poIdFromUrl = searchParams.get('po');
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [poSearch, setPOSearch] = useState('');
  const [showPODropdown, setShowPODropdown] = useState(false);
  
  const [formData, setFormData] = useState({
    purchase_order_id: '',
    receive_date: new Date().toISOString().split('T')[0],
    reference_number: '',
    items: [],
    notes: ''
  });

  const bid = businessId || activeBusiness?.id;

  useEffect(() => {
    if (bid) {
      fetchPurchaseOrders();
    }
  }, [bid]);

  useEffect(() => {
    if (poIdFromUrl && purchaseOrders.length > 0) {
      const po = purchaseOrders.find(p => p.id === poIdFromUrl);
      if (po) {
        handlePOSelect(po);
      }
    }
  }, [poIdFromUrl, purchaseOrders]);

  const fetchPurchaseOrders = async () => {
    try {
      const [res1, res2] = await Promise.all([
        api.get('/purchase-orders?status=CONFIRMED'),
        api.get('/purchase-orders?status=PARTIALLY_RECEIVED')
      ]);
      const confirmedOrders = res1.data.orders || [];
      const partialOrders = res2.data.orders || [];
      setPurchaseOrders([...confirmedOrders, ...partialOrders]);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load purchase orders'));
    }
  };

  const handlePOSelect = (po) => {
    setSelectedPO(po);
    setPOSearch(po.order_number);
    setShowPODropdown(false);
    
    // Map PO items to receive items
    const receiveItems = (po.items || []).map(item => ({
      po_item_id: item.id,
      item_id: item.item_id,
      item_name: item.item_name,
      item_code: item.item_code || '',
      hsn_code: item.hsn_code || '',
      ordered_quantity: item.quantity,
      received_quantity: item.quantity - (item.received_quantity || 0), // Default to pending qty
      unit: item.unit,
      rate: item.rate,
      tax_rate: item.tax_rate || 0,
      tax_amount: 0,
      amount: 0
    })).map(item => {
      // Calculate amounts
      const lineTotal = item.received_quantity * item.rate;
      const taxAmount = lineTotal * (item.tax_rate / 100);
      return {
        ...item,
        tax_amount: Math.round(taxAmount * 100) / 100,
        amount: Math.round((lineTotal + taxAmount) * 100) / 100
      };
    });
    
    setFormData(prev => ({
      ...prev,
      purchase_order_id: po.id,
      items: receiveItems
    }));
  };

  const updateItem = (index, field, value) => {
    setFormData(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      
      // Recalculate amounts
      const qty = parseFloat(newItems[index].received_quantity) || 0;
      const rate = parseFloat(newItems[index].rate) || 0;
      const taxRate = parseFloat(newItems[index].tax_rate) || 0;
      
      const lineTotal = qty * rate;
      const taxAmount = lineTotal * (taxRate / 100);
      
      newItems[index].tax_amount = Math.round(taxAmount * 100) / 100;
      newItems[index].amount = Math.round((lineTotal + taxAmount) * 100) / 100;
      
      return { ...prev, items: newItems };
    });
  };

  // Calculate totals
  const subtotal = formData.items.reduce((sum, item) => sum + ((item.received_quantity || 0) * (item.rate || 0)), 0);
  const taxTotal = formData.items.reduce((sum, item) => sum + (item.tax_amount || 0), 0);
  const grandTotal = subtotal + taxTotal;

  const handleSave = async (asDraft = false) => {
    if (!formData.purchase_order_id) {
      toast.error('Please select a Purchase Order');
      return;
    }
    
    if (formData.items.every(item => (item.received_quantity || 0) <= 0)) {
      toast.error('Please enter received quantities for at least one item');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        subtotal,
        tax_total: taxTotal,
        grand_total: grandTotal,
        save_as_draft: asDraft
      };

      const response = await api.post('/purchase-receives', payload);
      navigate(`/business/${bid}/purchase-receives/${response.data.receive?.id}`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to save purchase receive'));
    } finally {
      setSaving(false);
    }
  };

  const filteredPOs = purchaseOrders.filter(po =>
    po.order_number?.toLowerCase().includes(poSearch.toLowerCase()) ||
    (po.supplier_name || po.party_name)?.toLowerCase().includes(poSearch.toLowerCase())
  );

  return (
    <div className="h-full flex flex-col bg-[#F8F9FB] animate-pageFromRight">
      {/* PREMIUM HEADER */}
      <div className="flex-shrink-0 bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate(`/business/${bid}/purchase-receives`)}
                className="group flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 hover:bg-slate-200 transition-all duration-200 active:scale-95"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600 group-hover:text-slate-800 transition-colors" />
              </button>
              <div>
                <h1 className="text-[18px] font-semibold text-slate-800">New Purchase Receive</h1>
                <p className="text-[12px] text-slate-500 mt-0.5">Record goods received from supplier</p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button 
                onClick={() => handleSave(true)} 
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 hover:border-slate-400 transition-all duration-200 text-[13px] font-medium active:scale-[0.98]"
              >
                <FileText className="w-4 h-4" /> Save Draft
              </button>
              <button 
                onClick={() => handleSave(false)} 
                disabled={saving}
                className="inline-flex items-center gap-2 px-5 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-all duration-200 text-[13px] font-medium shadow-sm shadow-teal-600/20 active:scale-[0.98]"
              >
                <Check className="w-4 h-4" /> {saving ? 'Saving...' : 'Confirm Receive'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-[1600px] mx-auto">
          <div className="grid grid-cols-12 gap-5">
            
            {/* LEFT COLUMN - 8 cols */}
            <div className="col-span-12 lg:col-span-8 space-y-4">
              
              {/* SELECT PURCHASE ORDER Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h2 className="text-[14px] font-semibold text-slate-800">Select Purchase Order</h2>
                    <p className="text-[11px] text-slate-500">Choose a confirmed PO to receive against</p>
                  </div>
                </div>
                
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text" 
                    value={poSearch}
                    onChange={(e) => { setPOSearch(e.target.value); setShowPODropdown(true); }}
                    onFocus={() => setShowPODropdown(true)}
                    className="w-full pl-11 pr-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-[14px] focus:bg-white focus:border-teal-500 focus:ring-0 focus:outline-none transition-all duration-200"
                    placeholder="Search by PO number or supplier..."
                  />
                  
                  {showPODropdown && (
                    <div className="absolute z-20 w-full mt-2 bg-white border border-slate-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                      {filteredPOs.length > 0 ? filteredPOs.slice(0, 8).map((po, idx) => (
                        <div key={po.id}
                          onClick={() => handlePOSelect(po)}
                          className={`px-4 py-3 hover:bg-teal-50 cursor-pointer transition-colors duration-150 ${idx !== 0 ? 'border-t border-slate-100' : ''}`}>
                          <div className="flex justify-between items-start">
                            <div>
                              <p className="text-[14px] font-medium text-slate-800">{po.order_number}</p>
                              <p className="text-[12px] text-slate-500 mt-0.5">{(po.supplier_name || po.party_name)}</p>
                            </div>
                            <span className="text-[13px] font-medium text-teal-600">{'\u20B9'}{(po.grand_total || 0).toLocaleString('en-IN')}</span>
                          </div>
                        </div>
                      )) : (
                        <div className="px-4 py-6 text-center">
                          <p className="text-[13px] text-slate-500">No confirmed purchase orders available</p>
                          <p className="text-[11px] text-slate-400 mt-1">Only CONFIRMED or PARTIALLY RECEIVED POs can be received against</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {selectedPO && (
                  <div className="mt-4 p-4 bg-teal-50/50 rounded-lg border border-teal-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[14px] font-semibold text-slate-800">{selectedPO.order_number}</p>
                        <p className="text-[12px] text-slate-600 mt-0.5">{(selectedPO.supplier_name || selectedPO.party_name)}</p>
                        <p className="text-[11px] text-slate-500 mt-1">{selectedPO.items?.length || 0} items • Order Total: ₹{(selectedPO.grand_total || 0).toLocaleString('en-IN')}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-[11px] font-medium ${selectedPO.status === 'PARTIALLY_RECEIVED' ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-700'}`}>
                        {selectedPO.status === 'PARTIALLY_RECEIVED' ? 'Partial' : 'Confirmed'}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* ITEMS TO RECEIVE Card */}
              {selectedPO && formData.items.length > 0 && (
                <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                      <Package className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h2 className="text-[14px] font-semibold text-slate-800">Items to Receive</h2>
                      <p className="text-[11px] text-slate-500">Enter received quantities</p>
                    </div>
                  </div>
                  
                  <div className="overflow-x-auto rounded-xl border border-slate-200">
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">#</th>
                          <th className="px-4 py-3 text-left text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Item</th>
                          <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Ordered</th>
                          <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Received</th>
                          <th className="px-4 py-3 text-center text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Unit</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Rate</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Tax %</th>
                          <th className="px-4 py-3 text-right text-[11px] font-semibold text-slate-600 uppercase tracking-wide">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {formData.items.map((item, index) => (
                          <tr key={index} className="hover:bg-slate-50/80 transition-colors duration-150">
                            <td className="px-4 py-3 text-[13px] text-slate-500 font-medium">{index + 1}</td>
                            <td className="px-4 py-3">
                              <p className="text-[14px] font-medium text-slate-800">{item.item_name}</p>
                              {item.hsn_code && <p className="text-[11px] text-slate-400">HSN: {item.hsn_code}</p>}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center px-2.5 py-1 bg-slate-100 rounded-lg text-[13px] font-medium text-slate-600">
                                {item.ordered_quantity}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <input 
                                type="number" 
                                value={item.received_quantity}
                                onChange={(e) => updateItem(index, 'received_quantity', parseFloat(e.target.value) || 0)}
                                max={item.ordered_quantity}
                                min={0}
                                className="w-20 px-3 py-2 bg-emerald-50 border-2 border-emerald-200 rounded-lg text-[13px] text-center font-medium text-emerald-700 focus:outline-none focus:border-emerald-500 transition-all"
                              />
                            </td>
                            <td className="px-4 py-3 text-center text-[13px] text-slate-600">{item.unit}</td>
                            <td className="px-4 py-3 text-right text-[13px] text-slate-600">₹{(item.rate || 0).toFixed(2)}</td>
                            <td className="px-4 py-3 text-right text-[13px] text-slate-600">{item.tax_rate}%</td>
                            <td className="px-4 py-3 text-right">
                              <span className="text-[14px] font-semibold text-emerald-600">
                                ₹{(item.amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <p className="text-[12px] text-blue-700">💡 Partial receives are supported. You can receive items in multiple batches.</p>
                  </div>
                </div>
              )}

              {/* NOTES Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-slate-500 to-slate-600 flex items-center justify-center shadow-sm">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Notes</h2>
                </div>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-200 rounded-xl text-[14px] focus:bg-white focus:border-slate-400 focus:outline-none resize-none transition-all duration-200"
                  rows={3}
                  placeholder="Add notes about this receive..."
                />
              </div>
            </div>

            {/* RIGHT COLUMN - 4 cols */}
            <div className="col-span-12 lg:col-span-4 space-y-4">
              
              {/* RECEIVE DETAILS Card */}
              <div className="bg-white rounded-xl p-5 shadow-sm border border-slate-200/80 hover:shadow-md transition-shadow duration-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <FileText className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Receive Details</h2>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-2">Receive Number</label>
                    <input type="text" value="Auto-generated" disabled
                      className="w-full px-4 py-2.5 bg-slate-100 border-2 border-slate-200 rounded-xl text-[14px] text-slate-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-2">Receive Date</label>
                    <input type="date" value={formData.receive_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, receive_date: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-[12px] font-medium text-slate-600 mb-2">Reference Number</label>
                    <input type="text" value={formData.reference_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, reference_number: e.target.value }))}
                      className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-[13px] focus:outline-none focus:border-teal-500"
                      placeholder="GRN / Invoice ref"
                    />
                  </div>
                </div>
              </div>

              {/* SUMMARY Card */}
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-5 shadow-sm border border-emerald-200/60">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-sm">
                    <Package className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-[14px] font-semibold text-slate-800">Receive Summary</h2>
                </div>
                
                <div className="space-y-3">
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Subtotal</span>
                    <span className="text-slate-800 font-medium">₹{subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-[13px]">
                    <span className="text-slate-600">Tax</span>
                    <span className="text-emerald-600 font-medium">+ ₹{taxTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  
                  <div className="border-t border-emerald-200 pt-4 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-[14px] font-semibold text-slate-700">Total Value</span>
                      <span className="text-[20px] font-bold text-emerald-600">
                        ₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* INFO Card */}
              <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                <p className="text-[12px] text-amber-800 leading-relaxed">
                  <strong>Note:</strong> Confirming this receive will update your inventory stock levels automatically.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePurchaseReceivePage;
